import { DeleteOneCurrentFilterAction, ResetFilterBufferAction } from '@actions';
import { CommonModule } from '@angular/common';
import {
  Component, ComponentRef, EventEmitter, Input, NgModule,
  OnDestroy, OnInit, Output, Renderer2, ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AppState } from '@appStates';
import { ScreenDetectedService } from '@core/services';
import { EFiltersState } from '@enums';
import {
  FilterSearchPage, ICurrentFilter, IElement,
  IFilterChecked, ParamsPageSearch
} from '@interfaces';
import { TranslocoModule } from '@ngneat/transloco';
import { select, Store } from '@ngrx/store';
import {
  selectCurrentFilters, selectCurrentFiltersKeys,
  selectCurrentFiltersLength, seletStatusResetBufferFilter
} from '@selectors';
import { SharedModule } from '@shared';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FilterSearchBlockLocalStorageService } from 'src/app/core/services/storage.services/filter-search-block-local-storage.service';
import { LazyLoadComponentsService } from 'src/app/lazy-load-components-ui/factories/lazy-load-components.service';
import { ButtonModule } from '../../../modules-ui';

@Component({
  selector: 'app-filters-block-pages',
  templateUrl: './filters-block-pages.component.html',
  styleUrls: ['./filters-block-pages.component.sass']
})
export class FiltersBlockPagesComponent implements OnInit, OnDestroy {
  @Input() public container: ViewContainerRef;
  @Input() public containerRef: ComponentRef<any>;
  @Input() public data: Observable<Map<string, FilterSearchPage[]>>;
  @Output() public onChangeFilter: EventEmitter<any> = new EventEmitter();

  public bufferFilterMap: Map<string, Set<number>> = new Map();
  public filterState: EFiltersState = EFiltersState.all;
  public currentFilters: Observable<ICurrentFilter[]> = this.store.pipe(select(selectCurrentFilters));
  public currentFiltersLength: Observable<number> = this.store.pipe(select(selectCurrentFiltersLength));
  public currentFiltersKeys: Observable<Set<string>> = this.store.pipe(select(selectCurrentFiltersKeys));

  private componentDestroyer = new Subject<void>();

  constructor(
    private readonly screen: ScreenDetectedService,
    private readonly filterSearchService: FilterSearchBlockLocalStorageService,
    private readonly renderer: Renderer2,
    private readonly route: ActivatedRoute,
    private readonly lazy: LazyLoadComponentsService,
    private readonly store: Store<AppState>) { }

  ngOnInit(): void {
    this.bufferFilterMap = this.getDefaultFilterFromParametrs();
    this.store.pipe(
      select(seletStatusResetBufferFilter),
      takeUntil(this.componentDestroyer)
    ).subscribe((status: boolean) => {
      if (status) {
        this.bufferFilterMap = new Map();
        this.filterState = 0;
        this.store.dispatch(ResetFilterBufferAction({ status: false }));
      }
    });
  }

  public changeFilterTabsState(state: EFiltersState): void {
    this.filterState = state;
  }

  public changeFilter(section: string, element: IElement, filter: HTMLInputElement): void {
    this.filterChecked({ section, element, status: filter.checked });
  }

  public setFilter(map: Map<string, Set<number>>): void {
    this.onChangeFilter.emit(map);
  }

  public cancelFilterByClick(map: Map<string, Set<number>>, item: ICurrentFilter): void {
    map.get(item.group).delete(Number(item.id));
    if (map.get(item.group).size == 0) {
      map.delete(item.group);
    }
    this.onChangeFilter.emit(map);
  }

  public deleteFilterFromStore(idx: string): void {
    this.store.dispatch(DeleteOneCurrentFilterAction({ id: idx }));
  }

  public switch(e): void {
    const storage = new Set(this.filterSearchService.fsr.split(','));
    const operator = () => {
      this.filterSearchService.removeFsr();
      this.filterSearchService.fsr = Array.from(storage).join(',');
    };
    if (storage.has(e)) { storage.delete(e); }
    else { storage.add(e); }
    operator();
  }

  public split(e): boolean {
    if (!this.filterSearchService.fsr) { this.filterSearchService.fsr = ''; }
    return !new Set(this.filterSearchService.fsr.split(',')).has(e);
  }

  public clearFilters(): void {
    this.onChangeFilter.emit(new Map());
    this.bufferFilterMap = new Map();
    // при очистке фильтров, находясь на выбранных фильтрах, включать таб со всеми фильтрами
    this.filterState = 0;
  }

  public closedFilters(): void {
    this.renderer.removeStyle(this.containerRef.location.nativeElement, 'animation');
    this.renderer.setStyle(this.containerRef.location.nativeElement, 'animation', 'sladeringFilterBlockOut 700ms');
    setTimeout(_ => this.lazy.clearContainer(this.container), 650);
  }

  public mobileClosedFilters(): void {
    if (this.screen.isMobile()) { this.closedFilters(); }
  }

  private filterChecked(data: IFilterChecked): void {
    if (data.status) {
      if (this.bufferFilterMap.has(data.section)) {
        this.bufferFilterMap.set(data.section,
          new Set(this.bufferFilterMap.get(data.section)).add(+data.element.id))
      } else {
        this.bufferFilterMap.set(data.section, new Set([+data.element.id]));
      }
    } else {
      if (this.bufferFilterMap.get(data.section).size === 1) {
        this.bufferFilterMap.delete(data.section);
      } else {
        const res = this.bufferFilterMap.get(data.section);
        res.delete(+data.element.id);
        this.bufferFilterMap.set(data.section, res);
      }
    }
    this.setFilter(this.bufferFilterMap);
  }

  private getDefaultFilterFromParametrs(): Map<string, Set<number>> {
    const exeption: Set<string> = new Set(['sectionId', 'info', 'pStart', 'pEnd', 'pattern']);
    const filterParamsMap: Map<string, Set<number>> = new Map();

    let queryParams: ParamsPageSearch;
    this.route.queryParams.pipe(take(1)).subscribe((params: ParamsPageSearch) => {
      queryParams = params;
    });

    for (const key in queryParams) {
      if (!exeption.has(key)) {
        filterParamsMap.set(
          key,
          new Set((queryParams[key].split(',')).map(item => +item))
        );
      }
    }

    return filterParamsMap;
  }

  ngOnDestroy(): void {
    this.componentDestroyer.next(null);
    this.componentDestroyer.complete();
  }
}

@NgModule({
  declarations: [FiltersBlockPagesComponent],
  imports: [
    CommonModule,
    TranslocoModule,
    SharedModule,
    ButtonModule,
    RouterModule
  ],
  exports: [FiltersBlockPagesComponent]
})
export class FilterBlockPagesModule { }
